import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CircleStop, ClipboardCheck, Eye, Pencil, Play, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useActivateCycleMutation, useCloseCycleMutation, useReviewCyclesQuery, useUpsertCycleMutation } from '@/client/api/hr/perf';
import { REVIEW_CYCLE_STATUS_BADGE, REVIEW_CYCLE_STATUS_LABEL, type ReviewCycle } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { DeleteConfirmDialog, EmptyState, ProgressBar, RowMenu, StatusBadge } from './components/hr-ui';
import { formatDay } from './components/leave-format';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

type Form = { name: string; startDate: string; endDate: string; dueDate: string };

const pct = (s?: { submitted: number; total: number }) => (s && s.total ? Math.round((s.submitted / s.total) * 100) : 0);

const ReviewCyclesPage = () => {
  const navigate = useNavigate();
  const cyclesQ = useReviewCyclesQuery();
  const upsert = useUpsertCycleMutation();
  const activate = useActivateCycleMutation();
  const close = useCloseCycleMutation();
  const canManage = useGate(PERMISSIONS.perf.manage).allowed;
  const [editing, setEditing] = useState<ReviewCycle | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>({ name: '', startDate: '', endDate: '', dueDate: '' });
  const [pending, setPending] = useState<{ cycle: ReviewCycle; kind: 'activate' | 'close' } | null>(null);

  const cycles = cyclesQ.data ?? [];
  const stats = useMemo(
    () => [
      { key: 'active', label: 'Active cycles', value: cycles.filter((c) => c.status === 'active').length },
      { key: 'upcoming', label: 'Upcoming', value: cycles.filter((c) => c.status === 'upcoming').length },
      {
        key: 'open',
        label: 'Reviews still open',
        value: cycles
          .filter((c) => c.status === 'active')
          .reduce((n, c) => n + (c.stats ? c.stats.self.total - c.stats.self.submitted + c.stats.manager.total - c.stats.manager.submitted : 0), 0),
      },
      { key: 'closed', label: 'Closed', value: cycles.filter((c) => c.status === 'closed').length },
    ],
    [cycles],
  );

  const openForm = (c: ReviewCycle | null) => {
    setEditing(c);
    setForm(c ? { name: c.name, startDate: c.startDate, endDate: c.endDate, dueDate: c.dueDate ?? '' } : { name: '', startDate: '', endDate: '', dueDate: '' });
    setOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      toast.error('Name, start and end are required');
      return;
    }
    const body =
      editing?.status === 'active'
        ? { id: editing.id, name: form.name.trim(), dueDate: form.dueDate || null }
        : { id: editing?.id, name: form.name.trim(), startDate: form.startDate, endDate: form.endDate, dueDate: form.dueDate || null };
    upsert.mutate(body, {
      onSuccess: () => {
        toast.success(editing ? 'Cycle updated' : 'Cycle created. Activate it to open the reviews.');
        setOpen(false);
      },
      onError: (err) => toast.error((err as Error).message || 'Could not save the cycle'),
    });
  };

  const confirm = () => {
    if (!pending) return;
    const { cycle, kind } = pending;
    const mut = kind === 'activate' ? activate : close;
    mut.mutate(cycle.id, {
      onSuccess: (res) =>
        toast.success(
          kind === 'activate'
            ? cycle.status === 'active'
              ? `${res.opened ?? 0} missing review${res.opened === 1 ? '' : 's'} opened`
              : `${cycle.name} is open: ${res.opened ?? 0} reviews to write`
            : `${cycle.name} closed. Manager reviews are now shared with each person.`,
        ),
      onError: (err) => toast.error((err as Error).message || 'Could not update the cycle'),
    });
    setPending(null);
  };

  return (
    <HrPageShell
      mock={false}
      title="Review cycles"
      description="Each cycle opens a self review for everyone and a manager review for everyone with a manager. Closing it shares the manager reviews."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Performance' }, { label: 'Review cycles' }]}
      icon={ClipboardCheck}
      actions={
        <PermissionButton code={PERMISSIONS.perf.manage} className="gap-2" onClick={() => openForm(null)}>
          <Plus className="h-4 w-4" /> New cycle
        </PermissionButton>
      }
      stats={stats}
    >
      {cyclesQ.isError ? (
        <EmptyState label={`Could not load cycles: ${(cyclesQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : cyclesQ.isLoading ? (
        <EmptyState label="Loading review cycles…" />
      ) : cycles.length === 0 ? (
        <EmptyState label="No review cycles yet. Create the first one." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Cycle</th>
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Due</th>
                <th className="px-4 py-3 font-medium">Self reviews</th>
                <th className="px-4 py-3 font-medium">Manager reviews</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cycles.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/human-resource/performance/cycles/${c.id}`)}>
                  <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDay(c.startDate)} – {formatDay(c.endDate)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.dueDate ? formatDay(c.dueDate) : '—'}</td>
                  {(['self', 'manager'] as const).map((k) => (
                    <td key={k} className="px-4 py-3">
                      {c.stats && c.stats[k].total ? (
                        <>
                          <p className="mb-1 text-xs text-gray-500">
                            {c.stats[k].submitted} / {c.stats[k].total}
                          </p>
                          <ProgressBar value={pct(c.stats[k])} className="w-24" />
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <StatusBadge label={REVIEW_CYCLE_STATUS_LABEL[c.status]} className={REVIEW_CYCLE_STATUS_BADGE[c.status]} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      label={`Actions for ${c.name}`}
                      items={[
                        { label: 'Open', icon: Eye, onSelect: () => navigate(`/admin/human-resource/performance/cycles/${c.id}`) },
                        { label: 'Edit', icon: Pencil, hidden: !canManage || c.status === 'closed', onSelect: () => openForm(c) },
                        { label: 'Activate', icon: Play, hidden: !canManage || c.status !== 'upcoming', onSelect: () => setPending({ cycle: c, kind: 'activate' }) },
                        { label: 'Add missing reviews', icon: Play, hidden: !canManage || c.status !== 'active', onSelect: () => setPending({ cycle: c, kind: 'activate' }) },
                        { label: 'Close', icon: CircleStop, danger: true, hidden: !canManage || c.status !== 'active', onSelect: () => setPending({ cycle: c, kind: 'close' }) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New review cycle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Name</span>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} placeholder="H2 2026" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Period start</span>
                <input
                  type="date"
                  disabled={editing?.status === 'active'}
                  value={form.startDate}
                  onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                  className={inputClass}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Period end</span>
                <input
                  type="date"
                  disabled={editing?.status === 'active'}
                  value={form.endDate}
                  min={form.startDate || undefined}
                  onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                  className={inputClass}
                />
              </label>
            </div>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Reviews due (optional)</span>
              <input type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} className={inputClass} />
            </label>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!pending}
        onOpenChange={(v) => !v && setPending(null)}
        title={
          pending?.kind === 'close'
            ? `Close ${pending.cycle.name}?`
            : pending?.cycle.status === 'active'
              ? 'Open reviews for people added since?'
              : `Activate ${pending?.cycle.name}?`
        }
        description={
          pending?.kind === 'close'
            ? 'Reviews can no longer be written or changed. Submitted manager reviews become visible to the people they are about. This cannot be undone.'
            : 'Everyone gets a self review, and everyone with a manager gets a manager review. They write them in My workplace.'
        }
        actionLabel={pending?.kind === 'close' ? 'Close cycle' : 'Activate'}
        danger={pending?.kind === 'close'}
        onConfirm={confirm}
      />
    </HrPageShell>
  );
};

export default ReviewCyclesPage;
